<template>
  <div class="map-container">

    <base-map
      :map-zoom="map.zoom"
      :map-center="map.center">

      <template #map-extras>
        <l-geo-json v-for="(zone, index) in zones"
                    :geojson="zone.geomPolygonGeojson"
                    :key="`zone-${index}`"
                    :options="getGeoJSONLayerOptions(zone)">
        </l-geo-json>

        <l-marker v-for="(zone, index) in formattedZones"
                  :key="`graph-${index}`"
                  :options="{zone: zone, riseOnHover: true}"
                  :permanent="true"
                  :lat-lng="getConvertedPointCoordinates(zone)"
                  :icon="getGraphIcon(zone)"
                  @click="evt => handleMarkerClick(zone)"
                  @ready="evt => handleMarkerReady(zone)">
        </l-marker>
      </template>
    </base-map>

  </div>
</template>

<script>
import L from 'leaflet';
import {
  LGeoJson,
  LMarker,
} from 'vue2-leaflet';
import BaseMap from '@/base_components/BaseMap';


export default {
  name: 'zone-map',

  components: {
    BaseMap,
    LGeoJson,
    LMarker,
  },

  props: {
    zones: {
      type: Array,
      default: () => ([]),
    },

    stats: {
      type: Object,
      default: () => ({}),
    },

    percentage: {
      type: Boolean,
      default: false,
    },

    miniCharts: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      graph: {
        sectionName: 'vesselCount',
        paddingValue: 5,
      },

      formattedZones: [],

      map: {
        zoom: 2,
        center: [47.41322, -1.219482],
        instance: null,
      },

      styles: {
        region: {
          weight: 2,
          color: '#2E93FA',
          dashArray: '',
          fillOpacity: 0.1
        },
      },
    };
  },

  updated() {
    if(this.miniCharts) {
      this.redrawMarkerGraphs();
    }
  },

  methods: {
    getFormattedZones() {
      return Object.keys(this.stats).map(zoneId => {
        return this.stats[zoneId].zone
      });
    },

    redrawMarkerGraphs() {
      this.formattedZones.forEach(zone => {
        const graphId = this.getGraphId(zone);
        const canvasContainerElement = document.getElementById(graphId).querySelector('.canvas-container');

        if(!!zone.ready && !canvasContainerElement.querySelector('canvas')) {
          this.handleMarkerReady(zone);
        }
      });
    },

    getGeoJSONLayerOptions() {
      return {
        style: this.styles.region,
      };
    },

    handleMarkerReady(zone) {
      if(this.miniCharts) {
        const canvasElement = this.createCanvasElement(zone);
        let context = canvasElement.getContext('2d');
        this.drawGraph(context, zone);
      }
      zone.ready = true;
    },

    async handleMarkerClick(zone) {
      this.$emit('show-graph', zone);
    },

    drawGraph(context, zone) {
      const width = context.canvas.width;
      const height = context.canvas.height;

      let values = this.getNormalizedValues(
        this.stats[zone.zoneId][this.graph.sectionName],
        height - this.graph.paddingValue,
      );
      const step = width / values.length;

      let currentX = 0;
      let invertedValue;
      context.beginPath();
      context.strokeStyle = '#b3b3b3';
      values.forEach((value, index) => {
        currentX += step;
        invertedValue = height - value;

        if(index) {
          context.lineTo(currentX, invertedValue);
        } else {
          context.moveTo(currentX, invertedValue);
        }
      });
      context.stroke();
    },

    getNormalizedValues(values, maxYValue) {
      const minZoneValue = Math.min.apply(null, values);
      let normalizedValues = values.map(
        // normalize y axis from bottom, lowest value becomes "0" on the y axis
        value => value - minZoneValue + this.graph.paddingValue
      );

      const maxZoneValue = Math.max.apply(null, normalizedValues);
      return normalizedValues.map(value => {
        // normalize y axis from top, highest value becomes maxYValue on the y axis
        return (value * maxYValue / maxZoneValue);
      });
    },

    getConvertedPointCoordinates(zone) {
      const coordinates = zone.geomCoordinateGeojson.coordinates;
      return [coordinates[1], coordinates[0]];
    },

    getGraphIcon(zone) {
      let htmlElement = this.miniCharts ? this.getMiniChartElement(zone) : this.getCircleLabelElement(zone);

      return L.divIcon({
        html: htmlElement,
        iconSize: this.miniCharts ? [120, 80] : [35, 35],
        iconAnchor: this.miniCharts ? [60, 50] : [18, 18],
        className: 'inarea-icon'
      });
    },

    getMiniChartElement(zone) {
      let {difference, firstValue, lastValue} = this.getValuesStat(zone);
      let differenceColor = this.getDifferenceGradientColor(firstValue, lastValue, 70, 180);

      // canvas is created in "@ready" on a marker element (cause we don't know actual dimensions)
      let element = document.createElement('div');
      element.id = this.getGraphId(zone);
      element.className = 'zone-marker zone-graph';
      element.innerHTML = (`
        <div class="title map-overlay">
          ${zone.zoneName}
        </div>
        <div class="header">
            <div class="difference" style="color: ${differenceColor}">
              ${difference}
            </div>
            <div>${lastValue}</div>
        </div>
        <div class="canvas-container">&nbsp;</div>
      `);
      return element;
    },

    getCircleLabelElement(zone) {
      let {firstValue, lastValue} = this.getValuesStat(zone);

      let element = document.createElement('div');
      element.id = this.getGraphId(zone);
      element.className = `zone-marker zone-circle`;
      element.style.backgroundColor = this.getDifferenceGradientColor(firstValue, lastValue, 150, 90);

      element.innerHTML = (`
        <div class="title map-overlay">
          ${zone.zoneName}
        </div>
        <div class="content">
          ${lastValue}
        </div>
      `);
      return element;
    },

    getValuesStat(zone) {
      let values = this.stats[zone.zoneId][this.graph.sectionName];
      const lastValue = Math.round(values[values.length - 1]);
      const firstValue = Math.round(values[0]);

      let percentageSign = '';
      if(this.percentage) {
        percentageSign = '%'
        const maxValue = Math.max.apply(null, values) || 1;
        values = values.map(value => value * 100 / maxValue);
      }

      const lastValuePercent = Math.round(values[values.length - 1]);

      let difference =  Math.round(lastValuePercent - values[0]);  // lastValue - firstValue

      if(difference > 0) {
        // arrow-up, +sign, value, percent
        difference = `<strong>&#9652;</strong> +${difference}${percentageSign}`;
      } else if (difference < 0) {
        // arrow-down, -sign, value, percent
        difference = `<strong>&#9662;</strong> ${difference}${percentageSign}`;
      } else {
        difference = `<strong>&#9679;</strong> ${difference}${percentageSign}`;
      }

      return {difference, firstValue, lastValue};
    },

    getDifferenceGradientColor(firstValue, lastValue, multiplier=150, shift=90) {
      const difference = lastValue - firstValue;
      let gradient;

      if(firstValue && lastValue) {
         gradient = (lastValue - firstValue) / firstValue;
         gradient = Math.abs(gradient);
      } else {
        gradient = 0.01;
      }
      gradient = Math.log(gradient * 100) / Math.log(100);
      gradient = Math.abs((gradient * multiplier).toFixed(0)) + shift;

      if(difference > 0) {
        return `rgb(20, ${gradient}, 20)`;
      } else if(difference < 0) {
        return `rgb(${gradient}, 30, 30)`;
      } else {
        return `rgb(50, 50, 50)`;
      }
    },

    getGraphId(zone) {
      return `graph-${zone.zoneId}`;
    },

    createCanvasElement(zone) {
      const graphId = this.getGraphId(zone);
      const canvasContainerElement = document.getElementById(graphId).querySelector('.canvas-container');

      canvasContainerElement.innerHTML = (`
        <canvas width="${canvasContainerElement.clientWidth}"
                height="${canvasContainerElement.clientHeight}">
        </canvas>
      `);

      const canvasElement = canvasContainerElement.querySelector('canvas');
      let context = canvasElement.getContext('2d');
      context.strokeStyle = "#fff";
      context.lineWidth = 2;
      context.lineCap = 'round';

      return canvasElement;
    },
  },

  watch: {
    stats() {
      this.formattedZones = this.getFormattedZones();
    },
  },
}
</script>

<style scoped>
  .map-container >>> .vue2leaflet-map {
    height: 100%;
  }

  .map-container >>> .zone-graph {
    display: flex;
    flex-direction: column;
    justify-content: stretch;
    align-items: stretch;
    padding: 3px;
    z-index: 1;

    height: 80px;
    width: 120px;
  }

  .map-container >>> .zone-marker .title {
    display: none;
    position: absolute;
    padding: 0 5px;
    top: -25px;
    left: 50%;
    border-radius: 3px;
    transform: translate(-50%, 0);

    font-size: 14px;
    white-space: nowrap;
  }

  .map-container >>> .zone-marker:hover .title {
    display: block;
  }

  .map-container >>> .zone-graph .header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    padding: 0 5px;
    position: relative;
    font-size: 18px;
  }

  .map-container >>> .zone-graph .difference { font-size: 14px; }

  .map-container >>> .zone-graph .canvas-container,
  .map-container >>> .zone-graph .canvas-container canvas {
    width: 100%;
    height: calc(100% - 5px);
  }

  .map-container >>> .zone-circle {
    min-height: 35px;
    min-width: 35px;
    border-radius: 50%;
    color: white;
    border: 1px solid #383838;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .map-container >>> .zone-circle .content {
    font-weight: 600;
    font-size: 12px;
  }

</style>

<style scoped lang="scss">
  .map-container {
    display: flex;
    flex-direction: column;

    padding: 0;
    height: 100%;
  }
</style>
